import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError, formatDateTR } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { Wand2, Download, FileText, Printer, ArrowLeft, ListChecks, CheckCircle2, AlertTriangle, X, FileText as PdfIcon } from "lucide-react";
import { toast } from "sonner";

export default function ProductionListPage() {
  const [params] = useSearchParams();
  const ids = params.get("ids") || "";
  const idList = useMemo(() => ids.split(",").filter(Boolean), [ids]);
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    if (!ids) return;
    try {
      const { data } = await api.get(`/production-lists/preview`, { params: { ids } });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [ids]);

  if (!ids) return <div className="text-slate-500">Siparişler sayfasından seçim yapıp buraya gelin.</div>;
  if (!preview) return <div className="text-slate-500">Yükleniyor...</div>;

  const allGenerated = preview.orders.length > 0 && preview.orders.every(o => o.barcodes_generated);
  const anyPending = preview.orders.some(o => !o.barcodes_generated);

  const generateAll = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    try {
      const { data } = await api.post("/production-lists/generate-labels", { order_ids: idList });
      const failures = data.results.filter(r => !r.ok);
      if (failures.length === 0) {
        toast.success(`${data.total_created} adet barkod oluşturuldu`);
      } else {
        toast.warning(`${data.total_created} adet oluşturuldu — ${failures.length} sipariş atlandı`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setGenerating(false);
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/exports/labels`, {
        params: { order_ids: ids, fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `uretim_listesi_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} indirildi`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openLabels = () => {
    // Open labels page with multiple orders
    window.open(`/etiketler?order_ids=${ids}`, "_blank");
  };

  const autoPrint = () => {
    window.open(`/etiketler?order_ids=${ids}&autoprint=1`, "_blank");
  };

  return (
    <div>
      <Link to="/siparisler" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-700 mb-3" data-testid="back-orders">
        <ArrowLeft size={16} /> Siparişlere Dön
      </Link>
      <PageHeader
        title="Üretim Listesi"
        subtitle={`${preview.orders.length} sipariş — ${preview.total_expected_pieces} toplam barkod`}
      />

      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Sipariş" value={preview.orders.length} accent="bg-slate-900 text-white" />
        <Kpi label="Beklenen Barkod" value={preview.total_expected_pieces} accent="bg-blue-700 text-white" />
        <Kpi label="Oluşturulmuş" value={preview.total_generated} accent={preview.total_generated >= preview.total_expected_pieces ? "bg-emerald-500 text-white" : "bg-amber-100 border-amber-500"} />
        <Kpi label="Eksik" value={Math.max(0, preview.total_expected_pieces - preview.total_generated)} accent={preview.total_expected_pieces === preview.total_generated ? "bg-emerald-100 border-emerald-500" : "bg-red-100 border-red-500 text-red-800"} />
      </div>

      {/* Actions */}
      <div className="mb-6 flex gap-3 flex-wrap">
        <Link
          to={`/uretim-listesi/yazdir?ids=${ids}`}
          data-testid="pl-print-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-slate-900 hover:bg-black text-white font-bold text-sm uppercase"
        >
          <PdfIcon size={16}/> Üretim Listesi PDF
        </Link>
        {anyPending && (
          <button onClick={() => setConfirmOpen(true)} disabled={generating} data-testid="bulk-generate-btn" className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold uppercase tracking-wide">
            <Wand2 size={18} /> {generating ? "Oluşturuluyor..." : "Tüm Barkodları Oluştur"}
          </button>
        )}
        {allGenerated && (
          <>
            <button onClick={async () => {
              try {
                const res = await api.get(`/exports/labels-pdf`, { params: { order_ids: ids }, responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const w = window.open(url, "_blank");
                w?.addEventListener?.("load", () => setTimeout(() => w.print?.(), 500));
                toast.success("PDF hazırlandı");
              } catch (e) { toast.error(formatApiError(e)); }
            }} data-testid="bulk-pdf-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm">
              <Printer size={16} /> PDF ile Yazdır
            </button>
            <button onClick={openLabels} data-testid="bulk-preview-labels" className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-slate-800 hover:bg-slate-100 font-bold text-sm">
              <Printer size={16} /> Önizle
            </button>
            <button onClick={() => download("csv")} data-testid="bulk-csv-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm">
              <Download size={16} /> Toplu CSV
            </button>
            <button onClick={() => download("xml")} data-testid="bulk-xml-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm">
              <FileText size={16} /> Toplu XML
            </button>
          </>
        )}
      </div>

      {anyPending && (
        <div className="mb-6 p-4 border-l-4 border-amber-500 bg-amber-50 flex items-center gap-3">
          <AlertTriangle className="text-amber-700" />
          <div className="text-sm text-amber-900">
            <b>Bazı siparişlerin barkodları henüz oluşturulmadı.</b> Barkod üretmeden CSV/XML dışa aktarma yapamazsınız. Yukarıdaki butona basarak toplu oluşturun.
          </div>
        </div>
      )}

      {/* Orders */}
      <section>
        <h2 className="font-display font-bold uppercase tracking-wide text-sm text-slate-500 mb-3 inline-flex items-center gap-2"><ListChecks size={16}/> Siparişler</h2>
        <div className="card-industrial overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 border-b border-slate-300 text-xs uppercase tracking-widest text-slate-600">
                <th className="text-left px-4 py-3">Sipariş No</th>
                <th className="text-left px-4 py-3">Referans</th>
                <th className="text-left px-4 py-3">Müşteri</th>
                <th className="text-left px-4 py-3">Sevk Tarihi</th>
                <th className="text-left px-4 py-3">Ürünler</th>
                <th className="text-right px-4 py-3">Beklenen</th>
                <th className="text-right px-4 py-3">Oluşan</th>
                <th className="text-left px-4 py-3">Durum</th>
              </tr>
            </thead>
            <tbody>
              {preview.orders.map((o, i) => (
                <tr key={o.id} className={`border-b border-slate-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`} data-testid={`pl-order-${o.order_number}`}>
                  <td className="px-4 py-3">
                    <Link to={`/siparisler/${o.id}`} className="font-mono font-bold text-blue-700 hover:underline">{o.order_number}</Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-600">{o.reference || "—"}</td>
                  <td className="px-4 py-3 font-semibold">{o.customer_name}</td>
                  <td className="px-4 py-3 font-mono tabular-nums">{formatDateTR(o.requested_shipment_date)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {o.products.map((p, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-300 text-xs">
                          <b>{p.category}</b> {p.model} {p.size} {p.fabric && <span className="text-slate-500">·{p.fabric}</span>}
                          {p.notes && (
                            <span className="ml-1 px-1.5 py-0.5 bg-amber-100 border border-amber-500 text-amber-900 text-[10px] font-bold" title={p.notes}>
                              📝 {p.notes.length > 40 ? p.notes.slice(0, 40) + "…" : p.notes}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                    {o.notes && (
                      <div className="mt-1.5 p-1.5 bg-amber-50 border-l-4 border-amber-500 text-amber-900 text-xs" data-testid={`pl-order-notes-${o.order_number}`}>
                        <b className="uppercase tracking-widest text-[9px] mr-1">Sipariş Notu:</b>{o.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-display font-black tabular-nums">{o.expected_pieces}</td>
                  <td className="px-4 py-3 text-right font-display font-black tabular-nums text-emerald-700">{o.generated_pieces}</td>
                  <td className="px-4 py-3">
                    {o.barcodes_generated
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><CheckCircle2 size={16}/> Hazır</span>
                      : <span className="inline-flex items-center gap-1 text-amber-700 font-bold"><AlertTriangle size={16}/> Bekliyor</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setConfirmOpen(false)} data-testid="bulk-confirm-modal">
          <div className="bg-white border-2 border-slate-900 w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-600 text-white grid place-items-center shrink-0"><Wand2 size={22}/></div>
              <div>
                <h3 className="font-display font-black text-xl text-slate-900">Toplu Barkod Oluştur</h3>
                <p className="text-sm text-slate-600 mt-1">
                  <b className="text-blue-700">{preview.orders.length}</b> sipariş için toplam <b className="text-blue-700 font-mono">{preview.total_expected_pieces}</b> adet barkod oluşturulacak. Devam etmek ister misiniz?
                </p>
              </div>
              <button onClick={() => setConfirmOpen(false)} className="ml-auto text-slate-400 hover:text-slate-900"><X size={20}/></button>
            </div>
            <div className="max-h-72 overflow-y-auto border border-slate-300 mb-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-xs uppercase tracking-widest text-slate-600 border-b border-slate-300">
                    <th className="text-left px-3 py-2">Sipariş No</th>
                    <th className="text-left px-3 py-2">Müşteri</th>
                    <th className="text-right px-3 py-2">Beklenen</th>
                    <th className="text-left px-3 py-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.orders.map((o) => (
                    <tr key={o.id} className="border-b border-slate-200">
                      <td className="px-3 py-2 font-mono font-bold">{o.order_number}</td>
                      <td className="px-3 py-2 font-semibold">{o.customer_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{o.expected_pieces}</td>
                      <td className="px-3 py-2 text-xs">
                        {o.barcodes_generated
                          ? <span className="text-emerald-700 font-bold">Zaten üretilmiş — atlanacak</span>
                          : <span className="text-amber-700 font-bold">Üretilecek</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 border-2 border-slate-300 hover:border-slate-800 font-bold text-sm" data-testid="bulk-confirm-cancel">İptal</button>
              <button onClick={generateAll} disabled={generating} data-testid="bulk-confirm-generate" className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold uppercase text-sm">
                <CheckCircle2 size={16}/> {generating ? "Oluşturuluyor..." : "Onayla ve Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`p-5 border-2 rounded-none ${accent}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="font-display font-black text-4xl tabular-nums mt-1">{value}</div>
    </div>
  );
}
